import { useEffect, useState } from 'react';
import type { FallbackProps } from 'react-error-boundary';
import type { SetFieldType, SetOptional } from 'type-fest';
import { NetworkError, RateLimitError, trackError, ValidationError } from '#api/errors.ts';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { Spindown } from '#components/atoms/spindown/Spindown.tsx';
import { Snippet } from '#components/patterns/code/snippet/Snippet.tsx';
import { CenteredLayout } from '#components/patterns/layouts/centered/CenteredLayout.tsx';
import { useIsOnline } from '#hooks/useIsOnline.ts';
import styles from './Oops.module.css';

type Props = {
  resetButtonText?: string;
  className?: string;
  showErrorDetails: boolean;
} & SetOptional<SetFieldType<FallbackProps, 'error', unknown>, 'resetErrorBoundary'>;

/**
 * A {@link NetworkError} means the request never left the machine, which the generic copy would
 * misreport as a bug on our end. `isOnline` only distinguishes "your device has no network" from
 * "we could not reach the server", since a machine that is online can still fail to reach our api.
 */
function describeError(error: unknown, isOnline: boolean) {
  if (error instanceof NetworkError) {
    return isOnline ?
        {
          heading: 'Cannot reach Defined Networking',
          detail: 'The request did not reach our servers. Check your connection, or try again in a moment.',
        }
      : { heading: 'No internet connection', detail: 'This page could not load because your device is offline.' };
  }

  return {
    heading: 'Oops, something went wrong',
    detail: error instanceof RateLimitError ? 'This browser has been rate-limited.' : 'The error has been logged.',
  };
}

export function Oops({ className, resetButtonText = 'Refresh', showErrorDetails, error, resetErrorBoundary }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const isOnline = useIsOnline();
  const { heading, detail } = describeError(error, isOnline);

  useEffect(() => {
    if (error) {
      trackError(error);
    }
  }, [error]);

  return (
    <CenteredLayout className={className} preventNotifications={true}>
      <div role="alert">
        <h2 className="h3">{heading}</h2>
        <p className="p mb-0">{detail}</p>
        <p className="p mb-24">
          You can try refreshing or contacting us via{' '}
          <a className="a" href="mailto:support@defined.net?subject=Report%20a%20Bug">
            support@defined.net
          </a>
          .
        </p>
        <Button
          className="mb-24"
          variant="secondary"
          onClick={() => {
            resetErrorBoundary ? resetErrorBoundary() : window.location.reload();
          }}
        >
          {resetButtonText}
        </Button>
        {showErrorDetails && error ?
          <details
            onToggle={(event) => {
              const detailsElement = event.target as HTMLDetailsElement;
              setIsOpen(detailsElement.open);
            }}
          >
            <summary className={styles.Oops_summary}>
              <Spindown className="mr-8" isExpanded={isOpen} aria-hidden="true" />
              Error details:
            </summary>
            <Snippet>
              {error instanceof ValidationError ?
                // Just pull out the parts that are interesting, not the path and 'reworded'
                JSON.stringify(
                  error.errors.map((err) => ({ message: err.message, code: err.code })),
                  null,
                  2
                )
              : error instanceof Error ?
                error.message
              : JSON.stringify(error)}
            </Snippet>
          </details>
        : null}
      </div>
    </CenteredLayout>
  );
}
